import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export type RGB = [red: number, green: number, blue: number];

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Converts r, g, b (each an integer 0–255) to a 6-digit hex color string
 * prefixed with '#'.
 */
export const convertRgbToHex = (...[r, g, b]: RGB): string => {
  const toHexPair = (value: number, channel: "r" | "g" | "b"): string => {
    if (!Number.isInteger(value) || value < 0 || value > 255) {
      throw new Error(
        `Invalid ${channel} channel: ${value}. Expected an integer between 0 and 255.`,
      );
    }
    return value.toString(16).padStart(2, "0");
  };

  return "#" + toHexPair(r, "r") + toHexPair(g, "g") + toHexPair(b, "b");
};

/**
 * Converts a hex color string (3- or 6-digit, with or without leading '#')
 * to an [r, g, b] tuple.
 */
export const convertHexToRgb = (hex: string): RGB => {
  const normalized = hex
    .replace(/^#/, "")
    .replace(
      /^([a-f\d])([a-f\d])([a-f\d])$/i,
      (_m, r, g, b) => r + r + g + g + b + b,
    );

  const match = normalized.match(/.{2}/g);

  if (match?.length !== 3) {
    throw new Error(`Invalid hex color: "${hex}"`);
  }

  return match.map((x) => Number.parseInt(x, 16)) as RGB;
};
